import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import StudentDashboard from './pages/StudentDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ModuleViewer from './pages/ModuleViewer';
import TestPage from './pages/TestPage';
import CertificatePage from './pages/CertificatePage';
import ManageModulesPage from './pages/ManageModulesPage';
import EmployerSignup from './pages/EmployerSignup';
import PartnerPage from './pages/PartnerPage';
import FinancialAidPage from './pages/FinancialAidPage';
import PaymentSuccess from './pages/PaymentSuccess';
import BusinessCardsPage from './pages/BusinessCardsPage';
import PayrollPage from './pages/PayrollPage';
import PayrollCheckPrinter from './pages/PayrollCheckPrinter';
import OnlineCoursesPage from './pages/OnlineCoursesPage';
import OnlineCourseViewer from './pages/OnlineCourseViewer';
import ApprenticeshipAgreement from './pages/ApprenticeshipAgreement';
import PricingPage from './pages/PricingPage';
import AssociationPage from './pages/AssociationPage';
import BoardOfNursingPage from './pages/BoardOfNursingPage';
import VitalHealthAssociationPage from './pages/VitalHealthAssociationPage';
import MembershipDashboard from './pages/MembershipDashboard';
import MembershipSuccess from './pages/MembershipSuccess';
import MembershipDirectory from './pages/MembershipDirectory';
import MembershipLibrary from './pages/MembershipLibrary';
import DisclaimersPage from './pages/DisclaimersPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import VerifyCertificatePage from './pages/VerifyCertificatePage';
import Preview3DCubePage from './pages/Preview3DCubePage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import HireCNAPage from './pages/HireCNAPage';
import FloatingSocialButton from './components/FloatingSocialButton';

// Honors ?redirect= / ?return= so deep-links (Express Enrollment, etc.) survive login.
function LoginRedirect() {
  const [params] = useSearchParams();
  const target = params.get('redirect') || params.get('return') || '/dashboard';
  return <Navigate to={target} replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <FloatingSocialButton />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/dashboard" />} />
        <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <LoginRedirect />} />
        <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
        <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/dashboard" />} />
        <Route path="/register" element={!user ? <Register onLogin={handleLogin} /> : <Navigate to="/dashboard" />} />
        <Route path="/employers" element={<EmployerSignup />} />
        <Route path="/partner" element={<PartnerPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/association" element={<AssociationPage />} />
        <Route path="/medcare-board-of-nursing" element={<BoardOfNursingPage />} />
        <Route path="/vital-health-association" element={<VitalHealthAssociationPage />} />
        <Route path="/membership/dashboard" element={user ? <MembershipDashboard /> : <Navigate to="/login?return=/membership/dashboard" />} />
        <Route path="/membership/success" element={<MembershipSuccess />} />
        <Route path="/membership/cancel" element={<Navigate to="/association" />} />
        <Route path="/membership/directory" element={<MembershipDirectory />} />
        <Route path="/membership/library" element={user ? <MembershipLibrary /> : <Navigate to="/login?return=/membership/library" />} />
        <Route path="/disclaimers" element={<DisclaimersPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/verify/:code" element={<VerifyCertificatePage />} />
        <Route path="/preview/programs-cube" element={<Preview3DCubePage />} />
        <Route path="/financial-aid" element={<FinancialAidPage />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/business-cards" element={<BusinessCardsPage />} />
        <Route path="/payroll" element={<PayrollPage />} />
        <Route path="/hire-cna" element={<HireCNAPage />} />
        <Route path="/payroll-check" element={user && user.role === 'admin' ? <PayrollCheckPrinter /> : <Navigate to="/login" />} />
        
        {/* Dashboard - Role-based */}
        <Route
          path="/dashboard"
          element={
            user ? (
              user.role === 'admin' ? 
                <AdminDashboard user={user} onLogout={handleLogout} /> :
                <StudentDashboard user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        
        {/* Training Module Routes */}
        <Route
          path="/module/:moduleId"
          element={user ? <ModuleViewer user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        
        <Route
          path="/test/:moduleId"
          element={user && user.role === 'student' ? <TestPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        
        <Route
          path="/certificate"
          element={user && user.role === 'student' ? <CertificatePage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        
        {/* Admin Routes */}
        <Route
          path="/manage-modules"
          element={user && user.role === 'admin' ? <ManageModulesPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        
        {/* Online Courses Routes (gated on signed apprenticeship agreement) */}
        <Route
          path="/online-courses"
          element={
            user
              ? user.agreement_signed || user.role === 'admin'
                ? <OnlineCoursesPage />
                : <Navigate to={`/apprenticeship-agreement?next=${encodeURIComponent('/online-courses')}`} replace />
              : <OnlineCoursesPage />
          }
        />
        <Route
          path="/online-course/:courseId"
          element={
            user
              ? user.agreement_signed || user.role === 'admin'
                ? <OnlineCourseViewer />
                : <Navigate to={`/apprenticeship-agreement?next=${encodeURIComponent(window.location.pathname)}`} replace />
              : <Navigate to="/login" replace />
          }
        />

        {/* ETA-671 Apprenticeship Agreement (mandatory post-register) */}
        <Route
          path="/apprenticeship-agreement"
          element={
            user ? (
              <ApprenticeshipAgreement
                user={user}
                onAgreementSigned={() => {
                  const updated = { ...user, agreement_signed: true };
                  localStorage.setItem('user', JSON.stringify(updated));
                  setUser(updated);
                }}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
